import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Mail, ArrowLeft, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { authService } from '../../services/auth';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateEmail = (): boolean => {
    if (!email) {
      setValidationError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setValidationError('Please enter a valid email address');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authService.requestPasswordReset(email);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (validationError) {
      setValidationError(null);
    }
  };

  if (isSuccess) {
    return (
      <Card className="w-full max-w-md auth-card">
        <CardHeader className="text-center space-y-2 pb-6">
          <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl font-bold">Check your email</CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            We've sent a password reset link to <strong>{email}</strong>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Click the link in the email to reset your password. If you don't see it, check your spam folder.
          </p>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4 pt-6">
          <Button
            variant="outline"
            className="w-full h-12"
            onClick={onBackToLogin}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to login
          </Button>

          <button
            type="button"
            onClick={() => {
              setIsSuccess(false);
              setEmail('');
            }}
            className="text-sm text-primary hover:text-primary-hover transition-colors hover:underline"
          >
            Try a different email
          </button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md auth-card">
      <CardHeader className="text-center space-y-2 pb-6">
        <CardTitle className="text-2xl font-bold">Forgot your password?</CardTitle>
        <CardDescription className="text-base text-muted-foreground">
          Enter your email address and we'll send you a link to reset your password.
        </CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive" className="animate-bounce-in">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-3">
            <Label htmlFor="email" className="text-sm font-medium text-foreground">Email address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                className={`pl-10 h-12 input-enhanced ${
                  validationError
                    ? 'border-destructive focus:border-destructive ring-destructive/20'
                    : ''
                }`}
                autoFocus
              />
            </div>
            {validationError && (
              <p className="text-sm text-destructive animate-slide-down flex items-center">
                {validationError}
              </p>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col space-y-6 pt-6">
          <Button
            type="submit"
            className="w-full h-12 btn-primary text-base font-medium group relative overflow-hidden !text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                Send reset link
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            )}
          </Button>

          <div className="text-center text-sm">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-primary hover:text-primary-hover font-medium transition-colors hover:underline inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </button>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};
